import { FC, useEffect, useMemo, useState } from 'react';

import { useApprovalDecision } from 'src/use-cases/invoice/useApprovalDecision';
import { Questionnaire } from '../questionnaire/Questionnaire';

import { ApprovalResults } from './ApprovalResults';
import { ApprovalDecisionProps } from './types';
import { useShipmentSelector } from '../../ShipmentSelector';

export const ApprovalDecision: FC<ApprovalDecisionProps> = (props) => {
  const { invoiceSummary, title = '', questionnaireTheme, approvalType } = props;
  const { operationsFlows, canGiveOpsReview, operationsResponse, handleSubmit, isLoading } = useApprovalDecision(invoiceSummary);
  const { shipment } = useShipmentSelector();

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [key, setKey] = useState(0);

  const isApprovalPending = useMemo(() => {
    if (approvalType === 'ACCESSORIAL') {
      return shipment?.approvalTypeApprovalStatusMap?.ACCESSORIAL_CHARGE === 'PENDING';
    }
    if (approvalType === 'SERVICE') {
      return shipment?.approvalTypeApprovalStatusMap?.SERVICE_TYPE === 'PENDING';
    }
    return false;
  }, [shipment?.approvalTypeApprovalStatusMap, approvalType]);

  useEffect(() => {
    if (isApprovalPending) {
      setIsReadOnly(false);
    } else if (operationsFlows?.length > 0) {
      setIsReadOnly(true);
    }
  }, [isApprovalPending, operationsFlows]);

  const questionsList = useMemo(() => {
    if (approvalType === 'ACCESSORIAL') {
      const approvalItems = operationsFlows?.filter((item) => item?.approvalType === 'ACCESSORIAL_CHARGE');
      return approvalItems || [];
    }
    if (approvalType === 'SERVICE') {
      const approvalItems = operationsFlows?.filter((item) => item?.approvalType === 'SERVICE_TYPE');
      return approvalItems || [];
    }
    return null;
  }, [operationsFlows, approvalType]);

  const responseList = useMemo(() => {
    if (approvalType === 'ACCESSORIAL') {
      const approvalItems = operationsResponse?.filter((item) => item?.approvalType === 'ACCESSORIAL_CHARGE');
      return approvalItems || [];
    }
    if (approvalType === 'SERVICE') {
      const approvalItems = operationsResponse?.filter((item) => item?.approvalType === 'SERVICE_TYPE');
      return approvalItems || [];
    }
    return null;
  }, [operationsResponse, approvalType]);

  useEffect(() => {
    if (responseList?.length > 0) {
      setKey(new Date().getTime());
    }
  }, [responseList]);

  const isApprovalEditable = useMemo(
    () => canGiveOpsReview && (invoiceSummary?.currentMainRole === 'OPERATION' || invoiceSummary?.currentMainRole === 'SUPPLY_CHAIN'),
    [canGiveOpsReview, invoiceSummary?.currentMainRole]
  );

  return (
    <>
      {questionsList?.length > 0 && !isLoading && (
        <>
          {!isReadOnly && isApprovalEditable && (
            <Questionnaire
              title={title}
              questions={questionsList}
              answers={responseList}
              onSubmit={handleSubmit}
              key={`questionnaire-${key}-${responseList?.length}`}
              isEditable={isApprovalEditable}
              isReadOnly={isReadOnly}
              theme={questionnaireTheme || 'dispute'}
              onClickEdit={() => setIsReadOnly(false)}
              onCancel={() => setIsReadOnly(true)}
            />
          )}
          {(isReadOnly || !isApprovalEditable) && responseList?.length > 0 && (
            <ApprovalResults title={title} answers={responseList} onClickEdit={isApprovalEditable ? () => setIsReadOnly(false) : undefined} />
          )}
        </>
      )}
    </>
  );
};
